import { Link, useNavigate } from 'react-router-dom'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Button, HelpIcon, Panel, SectionLabel, Stat, Tooltip, clickable } from '@/components/ui'
import { fmtPct, fmtUsd, fmtBps } from '@/lib/utils'
import { PairGlyph } from '@/components/dashboard/TokenGlyph'
import { useLoops } from '@/hooks/useLoops'
import { carryPresentation } from '@/lib/loops'

// ─────────────────────────────────────────────────────────────────
// Leveraged looping. Every figure here is derived from the SAME on-chain reserve data the markets
// page renders - collateral factors, supply and borrow rates, live prices - rather than from a
// separate table of hardcoded strategy numbers. See src/lib/loops.js for the definitions and the
// maths, shared with the detail page.
//
// The list is a chooser now; the loop itself lives at /multiply/:id. That reversed an earlier
// judgement - see MultiplyDetailSection.jsx's own note for what changed and why.
// ─────────────────────────────────────────────────────────────────

export function MultiplySection() {
  const { loops } = useLoops()

  // Only surface loops that are actually operational - a swap venue is wired on-chain (`configured`).
  // A catalogue entry whose venue genesis has not pointed at a pool yet is not a live product, and
  // rendering it as a "$0.00 / 0.0x / Venue pending" row reads as a half-built launch rather than a
  // bare-bones one. Loops appear here as they actually go live, the same way Vaults reads
  // activeVaults() and Markets reads the listed reserves; until the first one is wired, the table
  // falls back to its own "No loops yet" empty state.
  const liveLoops = loops.filter((l) => l.configured)

  const totalSuppliedUsd = liveLoops.reduce((s, r) => s + r.suppliedUsd, 0)
  const totalBorrowedUsd = liveLoops.reduce((s, r) => s + r.borrowedUsd, 0)
  // Featured cards only earn their space when there are more loops than they show - with three or
  // fewer live, the strip just re-renders the whole table below it (same gate as Vaults Spotlight).
  const featured = liveLoops.length > 3 ? liveLoops.slice(0, 3) : []

  return (
    <div id="multiply">
      <div style={headerRow}>
        <SectionHeader
          title="Multiply"
          subtitle="Leverage your exposure in a single transaction. Each loop supplies collateral, borrows against it, and buys more."
        />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <SummaryPill label="Total Deposits" value={fmtUsd(totalSuppliedUsd)} />
          <SummaryPill label="Active Borrows" value={fmtUsd(totalBorrowedUsd)} />
        </div>
      </div>

      {featured.length > 0 && (
        <div style={featuredRow}>
          {featured.map((loop) => <FeaturedLoop key={`featured-${loop.id}`} loop={loop} />)}
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <Panel style={{ padding: 0 }}>
          <div style={{ padding: '20px 24px 0' }}>
            <SectionLabel>All loops</SectionLabel>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <LoopTable loops={liveLoops} />
          </div>
        </Panel>
      </div>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Link to="/markets" style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, textDecoration: 'none' }}>
          Every loop borrows from the same pool as Markets. See the underlying rates →
        </Link>
      </div>
    </div>
  )
}

/// The reference puts its best loops in cards above the table, and the pattern earns its place even
/// with a short list: the table answers "how do these compare", the cards answer "what should I
/// look at first". They are different questions.
function FeaturedLoop({ loop }) {
  const navigate = useNavigate()
  return (
    <Panel style={{ padding: 0 }}>
      <div
        {...clickable(() => navigate(`/multiply/${loop.id}`), { label: `Open ${loop.collateral.symbol}/${loop.debt.symbol} loop` })}
        style={{ padding: 24, cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <PairGlyph symbols={[loop.collateral.symbol, loop.debt.symbol]} size={40} />
            <span style={{ color: T.text, fontFamily: T.fontD, fontSize: T.fs.ui, fontWeight: 700 }}>{loop.name}</span>
          </div>
          <span style={kindPill}>{loop.kind}</span>
        </div>
        <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="mono" style={{
            color: TONE[carryPresentation(loop, loop.allInNetApyBps).tone],
            fontFamily: T.fontN, fontSize: T.fs.display, fontWeight: 700, letterSpacing: '-0.02em',
          }}>
            {fmtBps(loop.allInNetApyBps)}
          </span>
          <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>net APY at max leverage</span>
        </div>
        <div style={{ marginTop: 6, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          up to {loop.leverage.toFixed(1)}x · {fmtUsd(loop.liquidityUsd)} borrowable
          {loop.allInNetApyBps !== loop.netApyBps && ` · interest carry ${fmtBps(loop.netApyBps)}`}
        </div>
      </div>
    </Panel>
  )
}

function LoopTable({ loops }) {
  const navigate = useNavigate()

  return (
    <table style={table}>
      <thead>
        <tr>
          <th style={th}>Supply</th>
          <th style={th}>Borrow</th>
          <th style={{ ...th, textAlign: 'right' }}>
            <LabelWithHelp text="Net APY at max leverage" tooltip="Everything the position earns across the whole exposure, less everything the borrowed portion costs: interest on both legs, HORIZON rewards, and native collateral yield such as wstETH staking, at today's rates. Quoted on a $10,000 open, so a fresh reserve shows the rate its first opener would actually capture. A negative figure on a directional loop is the ordinary price of holding leveraged exposure." />
          </th>
          <th style={{ ...th, textAlign: 'right' }}>
            <LabelWithHelp text="Max Leverage" tooltip="Set by the collateral factor of the asset being looped: exposure converges to 1 / (1 - collateral factor). It is a ceiling the pool enforces, not a target." />
          </th>
          <th style={{ ...th, textAlign: 'right' }}>
            <LabelWithHelp text="Borrowable" tooltip="Unborrowed liquidity in the debt reserve. A loop cannot exceed this however high its leverage ceiling is." />
          </th>
          <th style={{ ...th, textAlign: 'right' }}>Supplied</th>
          <th style={th}></th>
        </tr>
      </thead>
      <tbody>
        {loops.length === 0 ? (
          <tr><td colSpan={7} style={{ ...td, color: T.textMuted }}>
            No loops available on this network yet.
          </td></tr>
        ) : loops.map((r) => (
          <tr key={r.id} {...clickable(() => navigate(`/multiply/${r.id}`), { label: `Open ${r.id} loop` })} style={loopRow}>
            <td style={td}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <PairGlyph symbols={[r.collateral.symbol, r.debt.symbol]} size={40} />
                <div>
                  <div style={{ color: T.text }}>{r.name}</div>
                  <div style={{ color: T.textMuted, fontSize: T.fs.caption }}>{r.kind}</div>
                </div>
              </div>
            </td>
            <td style={td}>{r.debt.symbol}</td>
            {/* Never clamped, and the COLOUR carries what the sign means, which differs by loop
                type. On a yield loop a negative figure is a warning: the borrow costs more than
                the collateral earns. On a directional loop it is the ordinary price of holding
                leveraged exposure, and alarm-red would misrepresent a working product. See
                `carryPresentation`. */}
            <td style={{ ...td, textAlign: 'right', color: TONE[carryPresentation(r, r.allInNetApyBps).tone] }}>
              {fmtBps(r.allInNetApyBps)}
            </td>
            <td style={{ ...td, textAlign: 'right' }}>{r.leverage.toFixed(1)}x</td>
            <td style={{ ...td, textAlign: 'right' }}>{fmtUsd(r.liquidityUsd)}</td>
            <td style={{ ...td, textAlign: 'right' }}>{fmtUsd(r.suppliedUsd)}</td>
            <td style={{ ...td, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              {r.configured
                ? <Button variant="ghost" style={rowBtn} onClick={() => navigate(`/multiply/${r.id}`)}>Open</Button>
                : <Tooltip content={<div style={{ maxWidth: 240, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5, whiteSpace: 'normal' }}>
                    No swap venue is configured for this pair yet, so a loop has nothing to trade through. Genesis points the router at a pool before it can open.
                  </div>}><span style={soonPill}>Venue pending</span></Tooltip>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LabelWithHelp({ text, tooltip }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {text}
      <Tooltip content={<div style={{ maxWidth: 240, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5, whiteSpace: 'normal' }}>{tooltip}</div>}>
        <HelpIcon text={tooltip} nativeTitle={false} />
      </Tooltip>
    </span>
  )
}

function SummaryPill({ label, value }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 10,
      padding: '10px 16px', borderRadius: T.r.pill,
      border: `1px solid ${T.border}`, background: T.bg2,
    }}>
      <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>{label}</span>
      <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }}>{value}</span>
    </div>
  )
}

// Shared tone map, so 'neutral' means the same colour everywhere the carry is rendered.
const TONE = { positive: T.positive, negative: T.negative, neutral: T.textMuted }

const headerRow = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  gap: 20, flexWrap: 'wrap', marginBottom: 8,
}
const featuredRow = {
  marginTop: 24, display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16,
}
const table = { width: '100%', borderCollapse: 'collapse', fontFamily: T.fontM, fontSize: T.fs.body }
const th = {
  textAlign: 'left', padding: '14px 24px', color: T.textMuted, fontSize: T.fs.caption,
  fontWeight: 400, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap',
}
const td = { padding: '16px 24px', color: T.text, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }
const loopRow = { cursor: 'pointer', transition: 'background 0.15s ease' }
const rowBtn = { padding: '7px 16px', fontSize: T.fs.caption }
const soonPill = {
  display: 'inline-block', padding: '4px 10px', borderRadius: 999,
  border: `1px solid ${T.border}`, color: T.textMuted, fontSize: T.fs.caption,
}
const kindPill = {
  padding: '3px 10px', borderRadius: T.r.pill, border: `1px solid ${T.border}`,
  color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
}
